"use client";

import { getPodcastSpeakerLabel } from "@/lib/podcasts/personas";
import { cn } from "@/lib/utils";
import { type PodcastTranscriptSegment } from "@/models/podcast";
import { Check, Mic2, Play, StopCircle, Volume2 } from "lucide-react";
import { type ReactNode } from "react";
import { useEffect, useMemo, useRef, useState } from "react";

export function PublicPodcastPlayer({
  languageName,
  description,
  segments,
  sidebar,
}: {
  languageName: string;
  description: string;
  segments: PodcastTranscriptSegment[];
  sidebar: ReactNode;
}) {
  const player = usePublicPodcastPlayback(segments);

  return (
    <>
      <section className="mt-8 rounded-lg border border-gray-200 bg-white p-6 shadow-sm sm:p-8">
        <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-[0.16em] text-orange-700">
          <Volume2 className="h-4 w-4" />
          Listen first
          <span className="rounded-full border border-orange-200 bg-orange-50 px-2 py-0.5 text-[10px] tracking-normal">
            Beta
          </span>
        </div>
        <h2 className="mt-3 font-serif text-3xl font-semibold text-gray-950">
          A short {languageName} listening chapter with transcript.
        </h2>
        <p className="mt-4 max-w-3xl leading-7 text-gray-700">{description}</p>
        <div className="mt-6 flex flex-wrap gap-2">
          {player.isPlaying ? (
            <button
              type="button"
              onClick={player.stop}
              className="inline-flex items-center gap-2 rounded-full bg-red-500 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-red-600"
            >
              <StopCircle className="h-4 w-4" />
              Stop
            </button>
          ) : (
            <button
              type="button"
              onClick={() => player.playFrom(0)}
              disabled={player.audioReadyCount === 0}
              className="inline-flex items-center gap-2 rounded-full bg-orange-500 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-orange-600 disabled:opacity-50"
            >
              <Play className="h-4 w-4" />
              Play full episode
            </button>
          )}
        </div>
        <SegmentProgressBar
          segments={segments}
          playingIndex={player.playingIndex}
          isPlaying={player.isPlaying}
          onPlaySegment={player.playFrom}
        />
      </section>

      <section className="mt-8 grid gap-6 lg:grid-cols-[1fr_18rem]">
        <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm sm:p-8">
          <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-[0.16em] text-orange-700">
            <Mic2 className="h-4 w-4" />
            Transcript
          </div>
          <h2 className="mt-3 font-serif text-3xl font-semibold text-gray-950">
            Read while you listen.
          </h2>
          <SegmentProgressBar
            segments={segments}
            playingIndex={player.playingIndex}
            isPlaying={player.isPlaying}
            onPlaySegment={player.playFrom}
          />
          <div className="mt-6 space-y-4">
            {segments.map((segment, index) => {
              const isActive = player.playingIndex === index && player.isPlaying;

              return (
                <article
                  key={segment.id}
                  ref={(element) => {
                    player.segmentRefs.current[index] = element;
                  }}
                  className={cn(
                    "scroll-mt-24 rounded-lg border p-4 transition-colors",
                    isActive ? "border-orange-300 bg-orange-50" : "border-gray-200 bg-gray-50"
                  )}
                >
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <p className="text-xs font-bold uppercase tracking-[0.14em] text-gray-500">
                      {index + 1}. {getPodcastSpeakerLabel(segment)}
                    </p>
                    {segment.audioUrl ? (
                      <button
                        type="button"
                        onClick={() => player.playFrom(index)}
                        className="inline-flex items-center gap-2 rounded-full bg-orange-500 px-3 py-1.5 text-xs font-bold text-white transition-colors hover:bg-orange-600"
                      >
                        <Play className="h-3.5 w-3.5" />
                        Play segment
                      </button>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-gray-500">
                        No audio
                      </span>
                    )}
                  </div>
                  <p className="mt-3 whitespace-pre-line text-base leading-8 text-gray-800">
                    {segment.text}
                  </p>
                  {segment.audioUrl ? (
                    <p className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-emerald-700">
                      <Check className="h-3.5 w-3.5" />
                      Audio ready
                    </p>
                  ) : null}
                </article>
              );
            })}
          </div>
        </div>

        {sidebar}
      </section>
    </>
  );
}

function SegmentProgressBar({
  segments,
  playingIndex,
  isPlaying,
  onPlaySegment,
}: {
  segments: PodcastTranscriptSegment[];
  playingIndex: number | null;
  isPlaying: boolean;
  onPlaySegment: (index: number) => void;
}) {
  return (
    <div className="mt-5 flex h-12 items-end gap-1">
      {segments.map((segment, index) => (
        <button
          key={segment.id}
          type="button"
          onClick={() => onPlaySegment(index)}
          disabled={!segment.audioUrl}
          className={cn(
            "min-w-0 flex-1 rounded-t transition-all",
            playingIndex === index && isPlaying
              ? "h-full bg-orange-400"
              : segment.audioUrl
                ? "h-7 bg-orange-200 hover:h-10 hover:bg-orange-300"
                : "h-2 bg-gray-200"
          )}
          aria-label={`Play segment ${index + 1}`}
        />
      ))}
    </div>
  );
}

function usePublicPodcastPlayback(segments: PodcastTranscriptSegment[]) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const segmentRefs = useRef<Array<HTMLElement | null>>([]);
  const [playingIndex, setPlayingIndex] = useState<number | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioReadyCount = useMemo(
    () => segments.filter((segment) => segment.audioUrl).length,
    [segments]
  );

  const stop = () => {
    audioRef.current?.pause();
    audioRef.current = null;
    setIsPlaying(false);
    setPlayingIndex(null);
  };

  const playFrom = async (index: number) => {
    const segment = segments[index];
    if (!segment?.audioUrl) return;

    audioRef.current?.pause();
    const audio = new Audio(segment.audioUrl);
    audioRef.current = audio;
    setPlayingIndex(index);
    setIsPlaying(true);
    audio.onended = () => {
      const nextIndex = segments.findIndex(
        (candidate, candidateIndex) => candidateIndex > index && candidate.audioUrl
      );
      if (nextIndex >= 0) {
        void playFrom(nextIndex);
      } else {
        setIsPlaying(false);
        setPlayingIndex(null);
      }
    };
    await audio.play();
  };

  useEffect(() => {
    if (!isPlaying || playingIndex === null) return;
    segmentRefs.current[playingIndex]?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  }, [isPlaying, playingIndex]);

  useEffect(() => stop, []);

  return {
    audioReadyCount,
    isPlaying,
    playingIndex,
    playFrom,
    segmentRefs,
    stop,
  };
}
